<script setup>
// Grade do espelho de vendas: uma linha por torre, uma sub-linha por andar
// (do topo pro térreo, como o `MirrorBuilder` já devolve), com filtros de
// torre/tipologia e a legenda de cores. Cada `UnitCell` carrega a torre e o
// andar antes de emitir `select` — é o único lugar que sabe essa relação,
// já que `unit_json` do backend não devolve `tower_name`/`floor`.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import UnitCell from './UnitCell.vue';

const props = defineProps({
  mirror: { type: Object, default: () => ({ towers: [] }) },
  // Modo seleção (Task 13, aba "Unidade" do negócio): só unidades
  // `available` ficam clicáveis — as demais aparecem normalmente (cor por
  // status), só que inertes. Sem afetar o modo padrão (admin do
  // empreendimento), onde toda unidade abre o drawer.
  selectable: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const towerFilter = ref('');
const typologyFilter = ref('');

const LEGEND = [
  { status: 'available', cls: 'bg-n-teal-9/15 border-n-teal-9/40' },
  { status: 'reserved', cls: 'bg-n-amber-9/20 border-n-amber-9/50' },
  { status: 'sold', cls: 'bg-n-blue-11 border-n-blue-11' },
  { status: 'blocked', cls: 'bg-n-slate-4 border-n-slate-6' },
];

const towers = computed(() => props.mirror?.towers || []);

const towerOptions = computed(() =>
  towers.value.map(tower => ({ id: tower.id, name: tower.name }))
);

const typologyOptions = computed(() => {
  const set = new Set();
  towers.value.forEach(tower => {
    tower.floors.forEach(floor => {
      floor.units.forEach(unit => {
        if (unit.typology) set.add(unit.typology);
      });
    });
  });
  return Array.from(set);
});

const matchesFilters = unit => {
  if (typologyFilter.value && unit.typology !== typologyFilter.value) {
    return false;
  }
  return true;
};

const visibleTowers = computed(() =>
  towers.value
    .filter(
      tower => !towerFilter.value || String(tower.id) === towerFilter.value
    )
    .map(tower => ({
      ...tower,
      floors: tower.floors.map(floor => ({
        ...floor,
        units: floor.units.filter(matchesFilters),
      })),
    }))
);

const selectUnit = (unit, tower, floor) => {
  emit('select', {
    ...unit,
    tower_id: tower.id,
    tower_name: tower.name,
    floor: floor.floor,
  });
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-wrap items-center gap-4">
      <label
        class="flex items-center gap-2 text-xs font-medium text-n-slate-11"
      >
        {{ t('EMPREENDIMENTOS.FILTERS.TOWER') }}
        <select
          v-model="towerFilter"
          class="px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12"
        >
          <option value="">{{ t('EMPREENDIMENTOS.FILTERS.ALL') }}</option>
          <option
            v-for="tower in towerOptions"
            :key="tower.id"
            :value="String(tower.id)"
          >
            {{ tower.name }}
          </option>
        </select>
      </label>

      <label
        class="flex items-center gap-2 text-xs font-medium text-n-slate-11"
      >
        {{ t('EMPREENDIMENTOS.FILTERS.TYPOLOGY') }}
        <select
          v-model="typologyFilter"
          class="px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12"
        >
          <option value="">{{ t('EMPREENDIMENTOS.FILTERS.ALL') }}</option>
          <option
            v-for="typology in typologyOptions"
            :key="typology"
            :value="typology"
          >
            {{ typology }}
          </option>
        </select>
      </label>

      <div class="flex items-center gap-3 ml-auto text-xs text-n-slate-11">
        <span class="font-medium">{{ t('EMPREENDIMENTOS.LEGEND.TITLE') }}</span>
        <span
          v-for="item in LEGEND"
          :key="item.status"
          class="flex items-center gap-1.5"
        >
          <span class="inline-block border rounded size-3" :class="item.cls" />
          {{ t(`EMPREENDIMENTOS.UNIT_STATUS.${item.status}`) }}
        </span>
      </div>
    </div>

    <p v-if="!towers.length" class="py-8 text-sm text-center text-n-slate-10">
      {{ t('EMPREENDIMENTOS.MIRROR.EMPTY') }}
    </p>

    <div v-else class="flex flex-col gap-6 overflow-x-auto">
      <div
        v-for="tower in visibleTowers"
        :key="tower.id"
        class="flex flex-col gap-2"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">{{ tower.name }}</h3>
        <div
          v-for="floor in tower.floors"
          :key="`${tower.id}-${floor.floor}`"
          class="flex items-start gap-2"
        >
          <span class="w-10 text-xs text-right text-n-slate-10 tabular-nums">
            {{ floor.floor }}
          </span>
          <div class="flex flex-wrap flex-1 gap-1.5">
            <UnitCell
              v-for="unit in floor.units"
              :key="unit.id"
              :unit="unit"
              :disabled="selectable && unit.status !== 'available'"
              @select="selectUnit($event, tower, floor)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
